import { useMemo } from "react";
import styled from "@emotion/styled"
import { useTranslation } from "react-i18next";

import {
  usePurchaseManagerErrorsRequest,
  usePackageManagerErrorsRequest,
  useChargebeeWebhookErrorsRequest,
  scannerErrorsRequest,
} from "@selfserve/react-common-data";
import { StyledText } from "@selfserve/react-ui";

import { AdminSpinner } from "../ui/admin-spinner";

const StyledErrorContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    display: 'flex',
    padding: `${theme?.spacing(3)} 0`,
    '&:first-of-type': {
      paddingTop: theme?.spacing(1),
    },
    '&:not(&:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
  })
);

const StyledErrorContainerText = styled(StyledText)(
  ({ theme }: { theme?: any }) => ({
    display: 'flex',
    minHeight: '100%',
    alignItems: 'center',
    minWidth: '240px',
    fontSize: theme.typography.pxToRem(18),
    borderRight: `1px solid ${theme.palette.grey[400]}`,
  })
);

const StyledPre = styled('pre')(
  ({ theme }: { theme?: any }) => ({
    margin: `0 0 0 ${theme?.spacing(3)}`,
  })
);

export const AdminErrors = () => {
  const { t } = useTranslation();
  const {
    data: { data: packageManagerErrors } = {},
    isLoading: isPackageManagerLoading,
  } = usePurchaseManagerErrorsRequest();
  const {
    data: { data: purchaseManagerErrors } = {},
    isLoading: isPurchaseManagerLoading
  } = usePackageManagerErrorsRequest();
  const {
    data: { data: chargebeeWebhookErrors } = {},
    isLoading: isChargebeeWebhookLoading,
  } = useChargebeeWebhookErrorsRequest();
  const {
    data: { data: scannerErrors } = {},
    isLoading: isScannerLoading,
  } = scannerErrorsRequest();

  const convertToJSON = (json: string) => {
    let value: null | string = null;
    try {
      value = JSON.stringify(JSON.parse(json), undefined, 2)
    } catch { }
    return value;
  };

  const packageManagerErrorsJSON = useMemo(() => convertToJSON(packageManagerErrors), [packageManagerErrors]);
  const purchaseManagerErrorsJSON = useMemo(() => convertToJSON(purchaseManagerErrors), [purchaseManagerErrors]);
  const chargebeeWebhookErrorsJSON = useMemo(() => convertToJSON(chargebeeWebhookErrors), [chargebeeWebhookErrors]);
  const scannerErrorsJSON = useMemo(() => convertToJSON(scannerErrors), [scannerErrors]);
  const isLoading = (
    isPackageManagerLoading ||
    isPurchaseManagerLoading ||
    isChargebeeWebhookLoading ||
    isScannerLoading
  );

  return (
    <>
      {packageManagerErrorsJSON && (
        <StyledErrorContainer>
          <StyledErrorContainerText>
            {t('common.package-manager-errors')}
          </StyledErrorContainerText>
          <StyledPre>{packageManagerErrorsJSON}</StyledPre>
        </StyledErrorContainer>
      )}
      {purchaseManagerErrorsJSON && (
        <StyledErrorContainer>
          <StyledErrorContainerText>
            {t('common.purchase-manager-errors')}
          </StyledErrorContainerText>
          <StyledPre>{purchaseManagerErrorsJSON}</StyledPre>
        </StyledErrorContainer>
      )}
      {chargebeeWebhookErrorsJSON && (
        <StyledErrorContainer>
          <StyledErrorContainerText>
            {t('common.chargebee-webhook-errors')}
          </StyledErrorContainerText>
          <StyledPre>{chargebeeWebhookErrorsJSON}</StyledPre>
        </StyledErrorContainer>
      )}
      {scannerErrorsJSON && (
        <StyledErrorContainer>
          <StyledErrorContainerText>
            {t('common.scanner-errors')}
          </StyledErrorContainerText>
          <StyledPre>{scannerErrorsJSON}</StyledPre>
        </StyledErrorContainer>
      )}
      <AdminSpinner loading={isLoading} fullScreen />
    </>
  );
};
